import type { WidgetSpec } from '@hero/schema';
import type { ConfirmationBatchResult } from './types';

const OBJECT_ID = /^[a-fA-F0-9]{24}$/;

/** Beyond this many order cards the outcome bubble folds into a toggle. */
const COLLAPSE_ORDER_CARDS_ABOVE = 3;

export interface ClientConfirmOutcome {
  text: string;
  widgets: WidgetSpec[];
  batchOutcomeCollapsed: boolean;
}

const linkClient = (name: string, clientId?: string): string => {
  if (clientId && OBJECT_ID.test(clientId)) {
    return `[${name}](hero://client/${clientId})`;
  }
  return name === 'Internal' ? '' : `**${name}**`;
};

const formatSingleClientCreated = (
  clientName: string | undefined,
  clientId: string | undefined,
  orderCount: number,
): string => {
  const name = clientName?.trim() || 'Internal';
  const clientPart = linkClient(name, clientId);
  const orderWord = orderCount === 1 ? 'order' : 'orders';

  if (orderCount === 1) {
    return clientPart ? `Created 1 order for ${clientPart}.` : 'Created 1 order.';
  }
  return clientPart
    ? `Created ${orderCount} ${orderWord} for ${clientPart}.`
    : `Created ${orderCount} ${orderWord}.`;
};

/**
 * One assistant outcome bubble per confirmed client card — "[Icon] ✓
 * Created 4 orders for Berain" with that client's order widgets.
 *
 * The "Created N orders" sentence is only ever synthesized for
 * `create_order`. Every other action (saving a place, adding a contact,
 * assigning a driver) answers to the backend's own result text, or to
 * its widget alone — synthesizing here produced "Created 0 orders for
 * <client>" under a saved-location card, because there were no
 * `order_card` widgets to count.
 */
export const buildClientConfirmOutcome = (
  result: ConfirmationBatchResult,
): ClientConfirmOutcome => {
  const orderCount = result.widgets.filter(w => w.type === 'order_card').length;
  const backendText = result.text?.trim();
  const text =
    backendText ||
    (result.action === 'create_order'
      ? formatSingleClientCreated(result.clientName, result.clientId, orderCount)
      : '');

  return {
    text,
    widgets: result.widgets,
    // The ack line no longer lists the lanes, so the cards ARE the
    // result — collapsing two of them hides the whole answer. Only a
    // genuine bulk run folds away.
    batchOutcomeCollapsed: orderCount > COLLAPSE_ORDER_CARDS_ABOVE,
  };
};

/**
 * @deprecated Prefer {@link buildClientConfirmOutcome} per card — kept
 * for any single merged summary callers.
 */
export const buildBatchConfirmOutcome = (
  results: ConfirmationBatchResult[],
): { text: string; widgets: WidgetSpec[]; collapseWidgets: boolean } => {
  const successful = results.filter(r => r.ok);
  const first = buildClientConfirmOutcome(successful[0] ?? results[0]);
  return {
    text: first.text,
    widgets: successful.flatMap(r => r.widgets),
    collapseWidgets: successful.length > 1,
  };
};
